import { invariant } from './commonUtils';

/**
 * Splits an array into two arrays based on a predicate.
 * @param items The array to split.
 * @param predicate The predicate to split the array by.
 *                  If the predicate returns true, the item is
 *                  added to the first array, otherwise the second.
 * @returns A tuple containing the two arrays.
 */
export function split<T, P extends T>(items: T[], predicate: (item: T) => item is P): [P[], T[]] {
  return items.reduce(
    (acc, item) => {
      if (predicate(item)) {
        acc[0].push(item);
      } else {
        acc[1].push(item);
      }
      return acc;
    },
    [[], []] as [P[], T[]],
  );
}

export function descending<T extends object | number>(selector: (item: T) => number): (a: T, b: T) => number {
  return (a: T, b: T) => selector(b) - selector(a);
}

export function ascending<T extends object | number>(selector: (item: T) => number | number[]): (a: T, b: T) => number {
  return (a: T, b: T) => {
    const aVal = selector(a);
    const bVal = selector(b);

    if (Array.isArray(aVal) && Array.isArray(bVal)) {
      return aVal[0] - bVal[0] || aVal[1] - bVal[1];
    }

    invariant(typeof aVal === 'number' && typeof bVal === 'number', 'Expected a number');

    return aVal - bVal;
  };
}
